import React from 'react';

import IconButton from '@mui/material/IconButton';

import { GridColDef, GridFilterOperator } from '@mui/x-data-grid';

import { AutocompleteRenderInputParams, SelectChangeEvent } from '@mui/material';

import { Dropdown } from 'components/Dropdown';

import Delete from 'components/Icons/Delete';

import { StyledTextField } from 'components/Input/styles';

import { PickerProps } from 'components/Grid/operators/types';

import { EmptyFilterValues } from 'components/Grid/operators/EmptyNotEmpty/EmptyNotEmpty';

import { ProtectorType } from 'core/constants';

import {
  ColumnsContainer,
  EntityContainer,
  EntityName,
  FilterItemContainer,
  OperatorContainer,
  InputContainer,
  StyledMenuItem,
  DisabledTextInput,
  StyledColumnAutocomplete,
} from './styles';
import { FilterItemProps } from './types';

const FilterItem = ({ columns, filter, onChange, onDelete }: FilterItemProps): React.ReactElement => {
  const column = (filter.field && columns.lookup[filter.field]) as
    | (GridColDef & { protectorType: ProtectorType })
    | null;

  if (column && column.protectorType === ProtectorType.Pipeline) {
    return <></>;
  }

  const operator = column?.filterOperators?.find((f) => f.label === filter.operator);
  const InputComponent = operator?.InputComponent as React.FunctionComponent<PickerProps>;

  const columnOptions = Object.values(columns.lookup).filter(
    (i) =>
      i.filterable &&
      // @ts-ignore
      i.protectorType !== ProtectorType.Pipeline
  );

  const onChangeColumn = (_: unknown, newValue: GridColDef | null): void => {
    if (!newValue) return;
    const newColumn = columns.lookup[newValue.field];
    const [newOperator] = newColumn.filterOperators as unknown as GridFilterOperator[];
    onChange({
      field: newColumn.field,
      operator: newOperator.label as string,
      value: null,
    });
  };

  const onChangeOperator = (event: SelectChangeEvent<unknown>): void => {
    if (event.target.value === EmptyFilterValues.is || event.target.value === EmptyFilterValues.isNot) {
      onChange({
        ...filter,
        operator: event.target.value as string,
        value: event.target.value === EmptyFilterValues.is ? 'empty' : 'not_empty',
      });
    } else {
      onChange({
        ...filter,
        operator: event.target.value as string,
        value: null,
      });
    }
  };

  const onChangeValue = (item: { value: unknown }): void => {
    let newValue = item.value;
    if (operator?.label === 'range' && (newValue as string[])[1] === null) {
      return;
    }
    if (operator?.label === 'is not') {
      newValue = `-${newValue}`;
    }

    onChange({
      ...filter,
      value: newValue,
    });
  };

  return (
    <FilterItemContainer>
      <EntityContainer>
        <EntityName>Columns</EntityName>
        <ColumnsContainer>
          <StyledColumnAutocomplete
            options={columnOptions}
            value={column || null}
            onChange={onChangeColumn}
            getOptionLabel={(option: GridColDef) => option.headerName || ''}
            isOptionEqualToValue={(option: GridColDef, val: GridColDef) => option.field === val.field}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <StyledTextField {...params} placeholder="Columns" />
            )}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: GridColDef) => (
              <StyledMenuItem {...props} key={option.field}>
                {option.headerName}
              </StyledMenuItem>
            )}
            fullWidth
            data-testid="filter-column"
            noOptionsText="No columns"
          />
        </ColumnsContainer>
      </EntityContainer>
      <EntityContainer>
        <EntityName>Operator</EntityName>
        <OperatorContainer>
          <Dropdown
            disabled={!column}
            fullWidth
            label="Operator"
            onChange={onChangeOperator}
            value={filter.operator}
            variant="outlined"
          >
            {column?.filterOperators?.map(({ label }) => (
              <StyledMenuItem key={label} value={label}>
                {label}
              </StyledMenuItem>
            ))}
          </Dropdown>
        </OperatorContainer>
      </EntityContainer>
      {operator?.value !== EmptyFilterValues.is && operator?.value !== EmptyFilterValues.isNot && (
        <EntityContainer>
          <EntityName>Value</EntityName>
          <InputContainer data-testid="filter-value">
            {operator ? (
              <InputComponent
                {...operator.InputComponentProps}
                applyValue={onChangeValue}
                item={{ value: filter.value }}
              />
            ) : (
              <DisabledTextInput disabled />
            )}
          </InputContainer>
        </EntityContainer>
      )}
      <IconButton
        onClick={onDelete}
        aria-label="Remove"
        size="large"
        sx={{
          alignSelf: 'flex-end',
          padding: '8px',
        }}
      >
        <Delete />
      </IconButton>
    </FilterItemContainer>
  );
};

export default FilterItem;
